import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Settings,
  Code,
  FolderGit2,
  X,
  AlertTriangle,
  FileText,
  Users,
  UserPlus,
  UserMinus,
} from "lucide-react";
import type { Project, Team } from "@/types/admin";
import adminService from "@/services/admin-service";
import Editor from "@monaco-editor/react";
import { Card, CardHeader, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { Permission } from "@/types";
import { usePagePermission } from "@/hooks/usePagePermission.tsx";

interface CreateProjectForm {
  project_name: string;
  team_id: string;
  description: string;
}

interface EditProjectForm {
  project_name: string;
  description: string;
}

interface DeleteConfirmation {
  project: Project;
  isOpen: boolean;
}

export default function ProjectsPage() {
  const { hasAccess, AccessDenied403 } = usePagePermission(
    Permission.READ_PROJECT,
  );
  const { hasPermission } = useAuthStore();

  // Check write/delete permissions
  const canWriteProjects = hasPermission(Permission.WRITE_PROJECT);
  const canDeleteProjects = hasPermission(Permission.DELETE_PROJECT);

  const [projects, setProjects] = useState<Project[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<string>("all");
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmation>({ project: {} as Project, isOpen: false });

  // Form states
  const [createForm, setCreateForm] = useState<CreateProjectForm>({
    project_name: "",
    team_id: "",
    description: "",
  });
  const [editForm, setEditForm] = useState<EditProjectForm>({
    project_name: "",
    description: "",
  });
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  // Configuration states
  const [configProject, setConfigProject] = useState<Project | null>(null);
  const [configurations, setConfigurations] = useState<Record<string, any>>({});
  const [newConfigKey, setNewConfigKey] = useState("");
  const [newConfigValue, setNewConfigValue] = useState("");
  const [editingConfig, setEditingConfig] = useState<{
    key: string;
    value: string;
    originalKey: string;
  } | null>(null);

  // Member management states
  const [membersProject, setMembersProject] = useState<Project | null>(null);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newMemberRole, setNewMemberRole] = useState("viewer");
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  // Helper function to detect language from file extension
  const getLanguageFromFilename = (filename: string): string => {
    const ext = filename.toLowerCase().split(".").pop();
    switch (ext) {
      case "yml":
      case "yaml":
        return "yaml";
      case "json":
        return "json";
      case "xml":
        return "xml";
      case "js":
      case "javascript":
        return "javascript";
      case "ts":
      case "typescript":
        return "typescript";
      case "py":
      case "python":
        return "python";
      case "toml":
        return "toml";
      case "ini":
        return "ini";
      case "md":
      case "markdown":
        return "markdown";
      case "txt":
      case "log":
        return "plaintext";
      default:
        return "plaintext";
    }
  };

  // Helper function to format value for display

  // Loading states
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Only load if user has permission
    if (hasAccess) {
      loadTeams();
      loadProjects();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const loadTeams = async () => {
    try {
      const teamsData = await adminService.listTeams();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch (err) {
      console.error("Error loading teams:", err);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listProjects(
        selectedTeam === "all" ? undefined : selectedTeam,
      );
      setProjects(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load projects");
      console.error("Error loading projects:", err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [selectedTeam]);

  // Member management functions
  const loadProjectMembers = async (projectId: string) => {
    try {
      setLoadingMembers(true);
      const members = await adminService.listProjectMembers(projectId);
      setProjectMembers(Array.isArray(members) ? members : []);
    } catch (err: any) {
      console.error("Error loading project members:", err);
      setError(err.response?.data?.detail || "Failed to load project members");
      setProjectMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Load all users in the organization (not just team members)
      // This ensures super admins and other users can be assigned
      const response = await adminService.listUsers();
      // Handle both paginated response and legacy array format
      const usersData = Array.isArray(response)
        ? response
        : response?.users || [];
      console.log("✅ Loaded users count:", usersData.length);
      if (usersData.length > 0) {
        console.log("First user sample:", usersData[0]);
      }
      setTeamMembers(usersData);
    } catch (err) {
      console.error("❌ Error loading users:", err);
      setTeamMembers([]);
    }
  };

  const handleOpenMembersModal = async (project: Project) => {
    setMembersProject(project);
    setShowMembersModal(true);
    setSelectedUser(null);
    await Promise.all([loadProjectMembers(project.project_id), loadAllUsers()]);
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!membersProject || !selectedUser) {
      setError("Please select a user");
      return;
    }

    try {
      setError(null);
      await adminService.addProjectMember(membersProject.project_id, {
        user_id: selectedUser.user_id,
        role: newMemberRole,
      });

      setSelectedUser(null);
      setNewMemberRole("viewer");
      await loadProjectMembers(membersProject.project_id);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to add member");
      console.error("Error adding member:", err);
    }
  };

  // Get available users (not already in project)
  const availableTeamMembers = teamMembers.filter(
    (teamMember) =>
      !projectMembers.some(
        (projectMember) => projectMember.user_id === teamMember.user_id,
      ),
  );

  console.log(
    "📊 Total users:",
    teamMembers.length,
    "Available:",
    availableTeamMembers.length,
  );

  const handleRemoveMember = async (userId: string) => {
    if (!membersProject) return;

    if (
      !confirm("Are you sure you want to remove this member from the project?")
    ) {
      return;
    }

    try {
      setError(null);
      await adminService.removeProjectMember(membersProject.project_id, userId);
      await loadProjectMembers(membersProject.project_id);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to remove member");
      console.error("Error removing member:", err);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.project_name || !createForm.team_id) {
      setError("Project name and team are required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await adminService.createProject({
        project_name: createForm.project_name,
        team_id: createForm.team_id,
        description: createForm.description || undefined,
      });

      setShowCreateModal(false);
      setCreateForm({ project_name: "", team_id: "", description: "" });
      await loadProjects();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create project");
      console.error("Error creating project:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject || !editForm.project_name) {
      setError("Project name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await adminService.updateProject(editingProject.project_id, {
        project_name: editForm.project_name,
        description: editForm.description || undefined,
      });

      setShowEditModal(false);
      setEditingProject(null);
      await loadProjects();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update project");
      console.error("Error updating project:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteConfirmation.project?.project_id) return;

    try {
      setSubmitting(true);
      setError(null);

      await adminService.deleteProject(deleteConfirmation.project.project_id);

      setDeleteConfirmation({ project: {} as Project, isOpen: false });
      await loadProjects();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete project");
      console.error("Error deleting project:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const loadProjectConfigurations = async (project: Project) => {
    try {
      const metadata = await adminService.getProjectMetadata(
        project.project_id,
      );
      setConfigurations(metadata || {});
    } catch (err) {
      console.error("Error loading configurations:", err);
      setConfigurations({});
    }
  };

  const openConfigModal = async (project: Project) => {
    setConfigProject(project);
    await loadProjectConfigurations(project);
    setShowConfigModal(true);
  };

  const handleAddConfiguration = async () => {
    if (!configProject || !newConfigKey.trim() || !newConfigValue.trim()) {
      setError("Configuration key and value are required");
      return;
    }

    // Validate file name
    if (!/^[a-zA-Z0-9_.-]+$/.test(newConfigKey)) {
      setError(
        "Configuration file name can only contain letters, numbers, dots, dashes, and underscores",
      );
      return;
    }

    // Check if key already exists
    if (configurations[newConfigKey]) {
      setError(
        `Configuration file "${newConfigKey}" already exists. Please use a different name or edit the existing one.`,
      );
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await adminService.setProjectMetadata(
        configProject.project_id,
        newConfigKey,
        newConfigValue,
      );
      setNewConfigKey("");
      setNewConfigValue("");
      await loadProjectConfigurations(configProject);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to add configuration");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateConfiguration = async () => {
    if (!configProject || !editingConfig) return;

    // Validate file name
    if (!/^[a-zA-Z0-9_.-]+$/.test(editingConfig.key)) {
      setError(
        "Configuration file name can only contain letters, numbers, dots, dashes, and underscores",
      );
      return;
    }

    // Check if new key name conflicts with existing (and it's not the original key)
    if (
      editingConfig.key !== editingConfig.originalKey &&
      configurations[editingConfig.key]
    ) {
      setError(
        `Configuration file "${editingConfig.key}" already exists. Please use a different name.`,
      );
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // If key name changed, delete old key first
      if (editingConfig.originalKey !== editingConfig.key) {
        await adminService.deleteProjectMetadata(
          configProject.project_id,
          editingConfig.originalKey,
        );
      }

      // Set the new/updated configuration
      await adminService.setProjectMetadata(
        configProject.project_id,
        editingConfig.key,
        editingConfig.value,
      );

      setEditingConfig(null);
      await loadProjectConfigurations(configProject);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update configuration");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfiguration = async (key: string) => {
    if (!configProject) return;

    // Confirm deletion
    if (
      !confirm(
        `Are you sure you want to delete the configuration file "${key}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await adminService.deleteProjectMetadata(configProject.project_id, key);
      await loadProjectConfigurations(configProject);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete configuration");
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({ project_name: "", team_id: "", description: "" });
    setShowCreateModal(true);
  };

  const openEditModal = (project: Project) => {
    setEditingProject(project);
    setEditForm({
      project_name: project.project_name,
      description: project.description || "",
    });
    setShowEditModal(true);
  };

  const openDeleteConfirmation = (project: Project) => {
    setDeleteConfirmation({ project, isOpen: true });
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find((t) => t.team_id === teamId);
    return team?.team_name || teamId;
  };

  const filteredProjects = (projects || []).filter(
    (project) =>
      project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const renderConfigValue = (value: any) => {
    if (typeof value === "string") {
      if (value.length > 100) {
        return value.substring(0, 100) + "...";
      }
      return value;
    }
    return JSON.stringify(value);
  };

  // Show 403 if no permission
  if (!hasAccess) {
    return <AccessDenied403 />;
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">Project Management</h1>
            <p className="text-lg text-orange-100">
              Manage projects, configurations, and team assignments
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-orange-100">Total Projects</p>
              <p className="text-2xl font-bold">{filteredProjects.length}</p>
            </div>
            {canWriteProjects && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              >
                <Plus className="h-4 w-4" />
                Create Project
              </button>
            )}
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                <option value="all">All Teams</option>
                {teams.map((team) => (
                  <option key={team.team_id} value={team.team_id}>
                    {team.team_name}
                  </option>
                ))}
              </select>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredProjects.length} projects
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Projects Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredProjects.map((project, index) => (
          <Card key={project.project_id} hover shadow="lg" className="group">
            <div className="relative">
              {/* Gradient header */}
              <div
                className={cn(
                  "h-20 rounded-t-xl bg-gradient-to-r",
                  index % 4 === 0 && "from-orange-400 to-red-600",
                  index % 4 === 1 && "from-blue-400 to-indigo-600",
                  index % 4 === 2 && "from-green-400 to-teal-600",
                  index % 4 === 3 && "from-purple-400 to-pink-600",
                )}
              >
                <div className="absolute -bottom-6 left-6">
                  <div
                    className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r text-white shadow-lg ring-4 ring-white dark:ring-gray-800",
                      index % 4 === 0 && "from-orange-500 to-red-600",
                      index % 4 === 1 && "from-blue-500 to-indigo-600",
                      index % 4 === 2 && "from-green-500 to-teal-600",
                      index % 4 === 3 && "from-purple-500 to-pink-600",
                    )}
                  >
                    <FolderGit2 className="h-6 w-6" />
                  </div>
                </div>
              </div>

              {/* Status badge */}
              <div className="absolute right-4 top-4">
                <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-green-800 shadow-sm">
                  <div className="mr-2 h-2 w-2 rounded-full bg-green-500" />
                  {project.status || "Active"}
                </span>
              </div>
            </div>

            <CardBody className="pt-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {project.project_name}
                  </h3>
                  {project.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                      {project.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                      <FolderGit2 className="h-4 w-4" />
                      <span>Team: {getTeamName(project.team_id)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                      <FileText className="h-4 w-4" />
                      <span>{project.stats?.total_requests || 0} requests</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                      <Settings className="h-4 w-4" />
                      <span>{project.stats?.api_key_count || 0} API keys</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-4">
                  <button
                    onClick={() => handleOpenMembersModal(project)}
                    className="flex-1 rounded-xl bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 text-sm font-medium text-blue-700 transition-all hover:from-blue-100 hover:to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 dark:text-blue-300 dark:hover:from-blue-800/50 dark:hover:to-blue-700/50"
                    title="Manage project members"
                  >
                    <Users className="mr-2 inline h-4 w-4" />
                    Members ({project.stats?.member_count || 0})
                  </button>
                  {canWriteProjects && (
                    <button
                      onClick={() => openConfigModal(project)}
                      className="flex-1 rounded-xl bg-gradient-to-r from-purple-50 to-purple-100 px-4 py-2 text-sm font-medium text-purple-700 transition-all hover:from-purple-100 hover:to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 dark:text-purple-300 dark:hover:from-purple-800/50 dark:hover:to-purple-700/50"
                    >
                      <Code className="mr-2 inline h-4 w-4" />
                      Configure
                    </button>
                  )}
                  {canWriteProjects && (
                    <button
                      onClick={() => openEditModal(project)}
                      className="rounded-xl p-2 text-blue-600 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title="Edit project"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  )}
                  {canDeleteProjects && (
                    <button
                      onClick={() => openDeleteConfirmation(project)}
                      className="rounded-xl p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Delete project"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Enhanced Empty State */}
      {filteredProjects.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-red-200 dark:from-orange-700 dark:to-red-600">
                <FolderGit2 className="h-12 w-12 text-orange-600 dark:text-orange-300" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No projects found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm || selectedTeam !== "all"
                  ? "Try adjusting your search term or team filter to find what you're looking for."
                  : "Projects help organize your API configurations and team workflows. Create your first project to get started."}
              </p>
              {!searchTerm && selectedTeam === "all" && canWriteProjects && (
                <button
                  onClick={openCreateModal}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-orange-600 hover:to-red-700"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Project
                </button>
              )}
              {(searchTerm || selectedTeam !== "all") && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedTeam("all");
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear filters
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-orange-500 to-red-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <Plus className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Create New Project
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Add a new project to organize your team workflows
                  </p>
                </div>

                <form onSubmit={handleCreateProject} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={createForm.project_name}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          project_name: e.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      placeholder="Enter project name"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Team <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={createForm.team_id}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          team_id: e.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="">Select a team</option>
                      {teams.map((team) => (
                        <option key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={createForm.description}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      placeholder="Describe the project's purpose"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-orange-600 hover:to-red-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Creating..." : "Create Project"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editingProject && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-blue-500 to-indigo-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <Edit2 className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowEditModal(false)}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Edit Project
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Update project information and settings
                  </p>
                </div>

                <form onSubmit={handleEditProject} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={editForm.project_name}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          project_name: e.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={editForm.description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && configProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 backdrop-blur-sm">
          <Card
            shadow="xl"
            className="flex max-h-[90vh] w-full max-w-6xl flex-col border-0 shadow-2xl"
          >
            <div className="relative">
              {/* Gradient header */}
              <div className="h-20 rounded-t-xl bg-gradient-to-r from-purple-500 to-indigo-600">
                <div className="absolute -bottom-6 left-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                    <Settings className="h-6 w-6" />
                  </div>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowConfigModal(false)}
                className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <CardBody className="flex min-h-0 flex-col pt-8">
              <div className="mb-8 flex-shrink-0">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Project Configuration: {configProject.project_name}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Manage configuration files and settings for this project
                </p>
              </div>

              <div
                className="min-h-0 flex-1 space-y-8 overflow-y-auto pr-2 focus:outline-none"
                tabIndex={0}
              >
                {/* Add New Configuration */}
                <Card className="border-purple-200/30 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-900/10 dark:to-indigo-900/10">
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Add New Configuration
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Create a new configuration file for your project
                    </p>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                          Configuration File Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., verifier.yml, config.json, settings.xml"
                          value={newConfigKey}
                          onChange={(e) => setNewConfigKey(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                          Configuration Content
                          {newConfigKey && (
                            <span className="ml-2 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                              {getLanguageFromFilename(newConfigKey)} format
                            </span>
                          )}
                        </label>
                        <div className="overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700">
                          <Editor
                            height="250px"
                            language={getLanguageFromFilename(newConfigKey)}
                            value={newConfigValue}
                            onChange={(value) => setNewConfigValue(value || "")}
                            options={{
                              minimap: { enabled: false },
                              fontSize: 13,
                              tabSize: 2,
                              insertSpaces: true,
                              wordWrap: "on",
                              scrollBeyondLastLine: false,
                              automaticLayout: true,
                              padding: { top: 16 },
                            }}
                            theme="vs-dark"
                          />
                        </div>
                      </div>

                      <button
                        onClick={handleAddConfiguration}
                        disabled={
                          submitting ||
                          !newConfigKey.trim() ||
                          !newConfigValue.trim()
                        }
                        className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-purple-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting
                          ? "Adding Configuration..."
                          : "Add Configuration"}
                      </button>
                    </div>
                  </CardBody>
                </Card>

                {/* Existing Configurations */}
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Existing Configurations
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {Object.keys(configurations).length} configuration files
                    </p>
                  </CardHeader>
                  <CardBody className="p-0">
                    {Object.keys(configurations).length === 0 ? (
                      <div className="px-6 py-12 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-900/20 dark:to-indigo-900/20">
                          <FileText className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                        </div>
                        <h5 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                          No configurations yet
                        </h5>
                        <p className="text-gray-500 dark:text-gray-400">
                          Add your first configuration file to get started with
                          project setup
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {Object.entries(configurations).map(([key, value]) => (
                          <div key={key} className="px-6 py-4">
                            {editingConfig?.key === key ? (
                              <div className="space-y-4">
                                <div>
                                  <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                                    File Name
                                  </label>
                                  <input
                                    type="text"
                                    value={editingConfig.key}
                                    onChange={(e) =>
                                      setEditingConfig({
                                        ...editingConfig,
                                        key: e.target.value,
                                      })
                                    }
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 font-mono text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                                  />
                                </div>

                                <div>
                                  <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                                    Content
                                    {editingConfig.key && (
                                      <span className="ml-2 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                        {getLanguageFromFilename(
                                          editingConfig.key,
                                        )}{" "}
                                        format
                                      </span>
                                    )}
                                  </label>
                                  <div className="overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700">
                                    <Editor
                                      height="350px"
                                      language={getLanguageFromFilename(
                                        editingConfig.key,
                                      )}
                                      value={editingConfig.value}
                                      onChange={(value) =>
                                        setEditingConfig({
                                          ...editingConfig,
                                          value: value || "",
                                        })
                                      }
                                      options={{
                                        minimap: { enabled: false },
                                        fontSize: 13,
                                        tabSize: 2,
                                        insertSpaces: true,
                                        wordWrap: "on",
                                        scrollBeyondLastLine: false,
                                        automaticLayout: true,
                                        padding: { top: 16 },
                                      }}
                                      theme="vs-dark"
                                    />
                                  </div>
                                </div>

                                <div className="flex gap-3">
                                  <button
                                    onClick={handleUpdateConfiguration}
                                    disabled={submitting}
                                    className="flex-1 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-green-600 hover:to-emerald-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {submitting
                                      ? "Saving Changes..."
                                      : "Save Changes"}
                                  </button>
                                  <button
                                    onClick={() => setEditingConfig(null)}
                                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="mb-2 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
                                      <FileText className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                                    </div>
                                    <div>
                                      <h6 className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                                        {key}
                                      </h6>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {getLanguageFromFilename(key)}{" "}
                                        configuration
                                      </p>
                                    </div>
                                  </div>
                                  <div className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-4 font-mono text-sm text-gray-600 dark:border-gray-700 dark:from-gray-900/50 dark:to-gray-800/50 dark:text-gray-400">
                                    {renderConfigValue(value)}
                                  </div>
                                </div>
                                <div className="ml-4 flex gap-2">
                                  <button
                                    onClick={() =>
                                      setEditingConfig({
                                        key,
                                        value:
                                          typeof value === "string"
                                            ? value
                                            : JSON.stringify(value, null, 2),
                                        originalKey: key,
                                      })
                                    }
                                    className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/20"
                                    title="Edit configuration"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteConfiguration(key)
                                    }
                                    disabled={submitting}
                                    className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:hover:bg-red-900/20"
                                    title="Delete configuration"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-red-500 to-pink-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-pink-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() =>
                    setDeleteConfirmation({
                      project: {} as Project,
                      isOpen: false,
                    })
                  }
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Delete Project
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone
                  </p>
                </div>

                <div className="mb-6">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      Are you sure you want to delete{" "}
                      <strong className="font-semibold">
                        {deleteConfirmation.project.project_name}
                      </strong>
                      ? This will permanently remove all project configurations,
                      API keys, and associated data.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteConfirmation({
                        project: {} as Project,
                        isOpen: false,
                      })
                    }
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteProject}
                    disabled={submitting}
                    className="flex-1 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-pink-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? "Deleting..." : "Delete Project"}
                  </button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Members Management Modal */}
      {showMembersModal && membersProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 backdrop-blur-sm">
          <Card
            shadow="xl"
            className="flex max-h-[90vh] w-full max-w-4xl flex-col border-0 shadow-2xl"
          >
            <CardHeader className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 p-2">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Project Members
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {membersProject.project_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMembersModal(false);
                    setMembersProject(null);
                    setProjectMembers([]);
                    setSelectedUser(null);
                    setNewMemberRole("viewer");
                    setError(null);
                  }}
                  className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>

            <CardBody className="flex-1 space-y-6 overflow-y-auto p-6">
              {error && (
                <div className="flex items-start space-x-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              )}

              {/* Add Member Form */}
              {hasPermission(Permission.WRITE_PROJECT) && (
                <Card className="overflow-visible border-blue-200/30 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10">
                  <CardHeader>
                    <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                      <UserPlus className="mr-2 h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Add New Member
                    </h3>
                  </CardHeader>
                  <CardBody className="overflow-visible">
                    <form onSubmit={handleAddMember} className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select User *
                          </label>
                          <select
                            value={selectedUser?.user_id || ""}
                            onChange={(e) => {
                              const user = teamMembers.find(
                                (u) => u.user_id === e.target.value,
                              );
                              setSelectedUser(user || null);
                            }}
                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            required
                          >
                            <option value="">Select a user...</option>
                            {availableTeamMembers.map((user) => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.user_name || user.user_email} (
                                {user.user_email})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Role *
                          </label>
                          <select
                            value={newMemberRole}
                            onChange={(e) => setNewMemberRole(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          >
                            <option value="viewer">Viewer (Read Only)</option>
                            <option value="editor">
                              Editor (Read & Write)
                            </option>
                            <option value="project_admin">
                              Project Admin (Full Control)
                            </option>
                          </select>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl"
                      >
                        <UserPlus className="mr-2 inline h-4 w-4" />
                        Add Member
                      </button>
                    </form>
                  </CardBody>
                </Card>
              )}

              {/* Members List */}
              <Card>
                <CardHeader>
                  <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                    <Users className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-400" />
                    Current Members ({projectMembers.length})
                  </h3>
                </CardHeader>
                <CardBody>
                  {loadingMembers ? (
                    <div className="py-8 text-center">
                      <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Loading members...
                      </p>
                    </div>
                  ) : projectMembers.length === 0 ? (
                    <div className="py-8 text-center">
                      <Users className="mx-auto mb-3 h-12 w-12 text-gray-400 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">
                        No members assigned to this project yet.
                      </p>
                      <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                        Add members using the form above.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {projectMembers.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center justify-between rounded-xl bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                        >
                          <div className="flex flex-1 items-center space-x-4">
                            {member.user_picture ? (
                              <img
                                src={member.user_picture}
                                alt={member.user_name || member.user_email}
                                className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 font-semibold text-white">
                                {member.user_name?.charAt(0).toUpperCase() ||
                                  member.user_email?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                {member.user_name || member.user_email}
                              </p>
                              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                {member.user_email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span
                              className={cn(
                                "rounded-full px-3 py-1 text-xs font-semibold",
                                member.role === "project_admin"
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                                  : member.role === "editor"
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
                              )}
                            >
                              {member.role === "project_admin"
                                ? "Admin"
                                : member.role === "editor"
                                  ? "Editor"
                                  : "Viewer"}
                            </span>
                            {hasPermission(Permission.WRITE_PROJECT) && (
                              <button
                                onClick={() =>
                                  handleRemoveMember(member.user_id)
                                }
                                className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Remove member"
                              >
                                <UserMinus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
